import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MapPin, 
  Clock, 
  Briefcase, 
  ArrowRight,
  Upload,
  X,
  CheckCircle,
  FileText,
  Users,
  Zap,
  Heart,
  Coffee,
  ChevronDown,
  ChevronUp,
  Search,
  Copy,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  {
    icon: Users,
    title: "Collaborative Culture",
    description: "Work with talented professionals in a supportive environment."
  },
  {
    icon: Zap,
    title: "Growth Opportunities",
    description: "Continuous learning and career development programs."
  },
  {
    icon: Heart,
    title: "Health & Wellness",
    description: "Comprehensive health insurance and wellness programs."
  },
  {
    icon: Coffee,
    title: "Work-Life Balance",
    description: "Flexible working hours and remote work options."
  }
];

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedTrackingId, setSubmittedTrackingId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [trackingEmail, setTrackingEmail] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const [trackingResults, setTrackingResults] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin: "",
    portfolio: "",
    cover_letter: "",
    resume: null
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load job listings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
    setIsSubmitted(false);
    setSubmittedTrackingId(null);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      linkedin: "",
      portfolio: "",
      cover_letter: "",
      resume: null
    });
  };

  const toggleJobExpand = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const handleTrackApplication = async () => {
    if (!trackingEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setIsTracking(true);
    try {
      let url = `${API}/track-application?email=${encodeURIComponent(trackingEmail)}`;
      if (trackingId) {
        url += `&tracking_id=${encodeURIComponent(trackingId)}`;
      }
      const response = await axios.get(url);
      setTrackingResults(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No applications found with the provided details");
        setTrackingResults([]);
      } else {
        toast.error("Failed to fetch application status");
      }
    } finally {
      setIsTracking(false);
    }
  };

  const copyTrackingId = (id) => {
    navigator.clipboard.writeText(id);
    toast.success("Tracking ID copied to clipboard");
  };

  const getStatusBadgeColor = (color) => {
    const colors = {
      yellow: "bg-yellow-100 text-yellow-800 border-yellow-300",
      blue: "bg-blue-100 text-blue-800 border-blue-300",
      green: "bg-green-100 text-green-800 border-green-300",
      red: "bg-red-100 text-red-800 border-red-300",
      purple: "bg-purple-100 text-purple-800 border-purple-300"
    };
    return colors[color] || colors.yellow;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF or Word document");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    
    setFormData(prev => ({ ...prev, resume: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone || !formData.resume) {
      toast.error("Please fill in all required fields and upload your resume");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append("job_id", selectedJob.id);
      submitData.append("job_title", selectedJob.title);
      submitData.append("full_name", formData.full_name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("linkedin", formData.linkedin || "");
      submitData.append("portfolio", formData.portfolio || "");
      submitData.append("cover_letter", formData.cover_letter || "");
      submitData.append("resume", formData.resume);

      const response = await axios.post(`${API}/applications`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setSubmittedTrackingId(response.data.tracking_id);
      setIsSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Careers</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-2 mb-6 leading-tight">
                Join Our Growing Team
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We're always looking for talented individuals who are passionate about 
                digital marketing and want to make an impact. Explore our open positions 
                and become part of our success story.
              </p>
              <Button
                onClick={() => setIsTrackingModalOpen(true)}
                variant="outline"
                className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                data-testid="track-application-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Track Your Application
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Why Join Us</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Benefits & Perks
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We believe in taking care of our team. Here's what you can expect when you join Omnilink.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 border border-slate-200 text-center card-hover"
              >
                <div className="w-14 h-14 bg-[#4f46e5]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-[#4f46e5]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Open Positions</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Current Opportunities
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Find your perfect role and start your journey with us.
            </p>
          </motion.div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#4f46e5] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-slate-600 mt-4">Loading positions...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Briefcase className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Open Positions</h3>
              <p className="text-slate-600">Check back later for new opportunities.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 hover:border-[#4f46e5] transition-colors overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-grow">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-slate-900">{job.title}</h3>
                          <span className="bg-[#4f46e5]/10 text-[#4f46e5] text-xs font-medium px-3 py-1 rounded-full">
                            {job.department}
                          </span>
                        </div>
                        <p className="text-slate-600 text-sm mb-4 line-clamp-2">{job.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {job.type}
                          </span>
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {job.experience}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => toggleJobExpand(job.id)}
                          data-testid={`view-details-btn-${job.id}`}
                          className="text-[#1e3a8a] border-[#1e3a8a]/30 hover:bg-[#1e3a8a]/5"
                        >
                          {expandedJob === job.id ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-1" />
                              Hide Details
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-1" />
                              View Details
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleApply(job)}
                          data-testid={`apply-btn-${job.id}`}
                          className="bg-[#1e3a8a] hover:bg-[#172554] text-white px-6 flex-shrink-0"
                        >
                          Apply Now
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Job Details */}
                  <AnimatePresence>
                    {expandedJob === job.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Full Description */}
                            <div>
                              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-[#4f46e5]" />
                                Job Description
                              </h4>
                              <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                                {job.description}
                              </p>
                            </div>
                            
                            {/* Requirements */}
                            {job.requirements && job.requirements.length > 0 && (
                              <div>
                                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-[#4f46e5]" />
                                  Requirements
                                </h4>
                                <ul className="space-y-2">
                                  {job.requirements.map((req, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                                      <span className="w-1.5 h-1.5 bg-[#4f46e5] rounded-full mt-2 flex-shrink-0"></span>
                                      {req}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Responsibilities */}
                            {job.responsibilities && job.responsibilities.length > 0 && (
                              <div className="md:col-span-2">
                                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                  <Briefcase className="w-4 h-4 text-[#4f46e5]" />
                                  Responsibilities
                                </h4>
                                <ul className="grid sm:grid-cols-2 gap-2">
                                  {job.responsibilities.map((resp, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                                      <span className="w-1.5 h-1.5 bg-[#4f46e5] rounded-full mt-2 flex-shrink-0"></span>
                                      {resp}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end">
                            <Button
                              onClick={() => handleApply(job)}
                              className="bg-[#1e3a8a] hover:bg-[#172554] text-white"
                            >
                              Apply for this Position
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {isSubmitted ? "Application Submitted!" : `Apply for ${selectedJob?.title}`}
            </DialogTitle>
            <DialogDescription>
              {isSubmitted 
                ? "Your application has been received successfully." 
                : "Fill out the form below to apply for this position."}
            </DialogDescription>
          </DialogHeader>

          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Application Submitted!</h3>
              <p className="text-slate-600 mb-4">
                Your application for <strong>{selectedJob?.title}</strong> has been received.
              </p>
              
              {submittedTrackingId && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6 max-w-sm mx-auto">
                  <p className="text-sm text-slate-500 mb-2">Your Tracking ID:</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="bg-white px-4 py-2 rounded border border-slate-200 text-lg font-mono font-bold text-[#1e3a8a]">
                      {submittedTrackingId}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyTrackingId(submittedTrackingId)}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Save this ID to track your application status anytime.
                  </p>
                </div>
              )}
              
              <p className="text-sm text-slate-500 mb-6">
                Our HR team will review your profile and get back to you soon.
              </p>
              
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsTrackingModalOpen(true);
                    setTrackingEmail(formData.email);
                  }}
                  variant="outline"
                  data-testid="track-my-application-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Track Application
                </Button>
                <Button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-[#1e3a8a] hover:bg-[#172554]"
                  data-testid="close-modal-btn"
                >
                  Done
                </Button>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 py-4">
              {/* Job Details */}
              {selectedJob && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-slate-900 mb-2">{selectedJob.title}</h4>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                    <span>{selectedJob.location}</span>
                    <span>{selectedJob.type}</span>
                    <span>{selectedJob.experience}</span>
                  </div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    data-testid="application-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    data-testid="application-email-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    required
                    data-testid="application-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                    data-testid="application-linkedin-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="portfolio">Portfolio / Website</Label>
                <Input
                  id="portfolio"
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleChange}
                  placeholder="https://yourportfolio.com"
                  data-testid="application-portfolio-input"
                />
              </div>

              {/* Resume Upload */}
              <div className="space-y-2">
                <Label>Resume *</Label>
                <div
                  className={`upload-zone rounded-lg p-6 text-center cursor-pointer ${
                    dragActive ? "drag-over" : ""
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="resume-upload-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                    data-testid="resume-file-input"
                  />
                  
                  {formData.resume ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-[#4f46e5]" />
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{formData.resume.name}</p>
                        <p className="text-sm text-slate-500">
                          {(formData.resume.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, resume: null }));
                        }}
                        className="p-1 hover:bg-slate-100 rounded"
                        data-testid="remove-resume-btn"
                      >
                        <X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">
                        Drag and drop your resume here
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        or click to browse (PDF, DOC, DOCX - Max 5MB)
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cover_letter">Cover Letter</Label>
                <Textarea
                  id="cover_letter"
                  name="cover_letter"
                  value={formData.cover_letter}
                  onChange={handleChange}
                  placeholder="Tell us why you're the perfect fit for this role..."
                  rows={4}
                  data-testid="application-cover-letter-input"
                  className="resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1"
                  data-testid="cancel-application-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-[#1e3a8a] hover:bg-[#172554] text-white"
                  data-testid="submit-application-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    "Submit Application"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Application Tracking Modal */}
      <Dialog open={isTrackingModalOpen} onOpenChange={setIsTrackingModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <Search className="w-6 h-6 text-[#1e3a8a]" />
              Track Your Application
            </DialogTitle>
            <DialogDescription>
              Enter your email address to check the status of your application(s).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Search Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tracking_email">Email Address *</Label>
                <Input
                  id="tracking_email"
                  type="email"
                  value={trackingEmail}
                  onChange={(e) => setTrackingEmail(e.target.value)}
                  placeholder="Enter the email used in your application"
                  data-testid="tracking-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tracking_id">Tracking ID (Optional)</Label>
                <Input
                  id="tracking_id"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                  placeholder="e.g., ABC12345"
                  className="font-mono"
                  data-testid="tracking-id-input"
                />
                <p className="text-xs text-slate-500">
                  Enter your tracking ID to view a specific application.
                </p>
              </div>
              <Button
                onClick={handleTrackApplication}
                disabled={isTracking || !trackingEmail}
                className="w-full bg-[#1e3a8a] hover:bg-[#172554]"
                data-testid="track-btn"
              >
                {isTracking ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Checking...
                  </span>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Check Status
                  </>
                )}
              </Button>
            </div>

            {/* Results */}
            {trackingResults.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 border-b pb-2">
                  Your Applications ({trackingResults.length})
                </h3>
                {trackingResults.map((app, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div>
                        <h4 className="font-semibold text-slate-900">{app.job_title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-500">Tracking ID:</span>
                          <code className="bg-white px-2 py-0.5 rounded text-xs font-mono font-bold text-[#1e3a8a] border">
                            {app.tracking_id}
                          </code>
                          <button
                            onClick={() => copyTrackingId(app.tracking_id)}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <Badge className={`${getStatusBadgeColor(app.status_color)} border`}>
                        {app.status_label}
                      </Badge>
                    </div>
                    
                    <div className="mt-3 p-3 bg-white rounded border border-slate-100">
                      <p className="text-sm text-slate-600">{app.status_message}</p>
                    </div>
                    
                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                      <span>Applied: {new Date(app.applied_date).toLocaleDateString("en-US", { 
                        year: "numeric", month: "short", day: "numeric" 
                      })}</span>
                      <span>Last Updated: {new Date(app.last_updated).toLocaleDateString("en-US", { 
                        year: "numeric", month: "short", day: "numeric" 
                      })}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Help Text */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-slate-500">
                Need help? Contact us at{" "}
                <a href="mailto:hr@omnilinksolutions.net" className="text-[#1e3a8a] hover:underline">
                  hr@omnilinksolutions.net
                </a>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Careers;
